"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import LegalFooter from "@/components/LegalFooter";

type Step = "phone" | "code";
type DevUser = { id: string; name: string; role: string; phone: string };

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginPageInner />
    </Suspense>
  );
}

function LoginPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const [step, setStep] = useState<Step>("phone");
  const [devUsers, setDevUsers] = useState<DevUser[]>([]);
  const [devEnabled, setDevEnabled] = useState(false);
  const [devLoading, setDevLoading] = useState(false);

  useEffect(() => {
    fetch("/api/auth/dev-login")
      .then((r) => r.json())
      .then((data: { enabled: boolean; users: DevUser[] }) => {
        setDevEnabled(!!data.enabled);
        setDevUsers(data.users ?? []);
      });
  }, []);

  async function devLogin(user: DevUser) {
    setDevLoading(true);
    const res = await fetch("/api/auth/dev-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: user.id }),
    });
    const data = await res.json();
    setDevLoading(false);
    if (!res.ok) {
      alert(data.error ?? "Fehler");
      return;
    }
    router.push(data.role === "ADMIN" ? "/admin" : "/chat");
  }

  async function seedDemo() {
    setDevLoading(true);
    await fetch("/api/auth/dev-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "seed" }),
    });
    const res = await fetch("/api/auth/dev-login");
    const data = await res.json();
    setDevUsers(data.users ?? []);
    setDevLoading(false);
  }
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState<"phone" | "email">("phone");
  const [userId, setUserId] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handlePhoneSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(method === "phone" ? { phone } : { email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUserId(data.userId);
      if (data.devCode) setDevCode(data.devCode);
      setStep("code");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Fehler");
    } finally {
      setLoading(false);
    }
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const tokenToRedeem = inviteToken ?? data.pendingInviteToken;
      if (tokenToRedeem && data.role !== "ADMIN") {
        const join = await fetch(`/api/invites/${tokenToRedeem}`, { method: "POST" });
        if (join.ok) {
          router.push("/chat");
          return;
        }
      }
      router.push(data.role === "ADMIN" ? "/admin" : "/chat");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Fehler");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">💬</div>
          <h1 className="text-2xl font-bold text-gray-800">KidChat</h1>
          <p className="text-gray-500 text-sm mt-1">Anmelden</p>
        </div>

        {step === "phone" ? (
          <form onSubmit={handlePhoneSubmit} className="space-y-4">
            <div>
              <div className="flex bg-gray-100 rounded-xl p-1 mb-2">
                <button
                  type="button"
                  onClick={() => setMethod("phone")}
                  className={`flex-1 py-1.5 text-sm rounded-lg transition ${
                    method === "phone"
                      ? "bg-white shadow-sm text-gray-800 font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  📱 Handy
                </button>
                <button
                  type="button"
                  onClick={() => setMethod("email")}
                  className={`flex-1 py-1.5 text-sm rounded-lg transition ${
                    method === "email"
                      ? "bg-white shadow-sm text-gray-800 font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  ✉️ E-Mail
                </button>
              </div>
              {method === "phone" ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Handynummer
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+49 123 456789"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    required
                  />
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-Mail-Adresse
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="du@beispiel.de"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    required
                  />
                </>
              )}
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 text-white rounded-xl py-3 font-semibold hover:bg-purple-700 transition disabled:opacity-50"
            >
              {loading ? "..." : "Code senden"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit} className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Code an <strong>{method === "phone" ? phone : email}</strong> gesendet
            </p>
            {devCode && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
                <p className="text-xs text-yellow-700 font-medium">Testmodus</p>
                <p className="text-2xl font-bold text-yellow-800 tracking-widest">{devCode}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                6-stelliger Code
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                maxLength={6}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-purple-400"
                required
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 text-white rounded-xl py-3 font-semibold hover:bg-purple-700 transition disabled:opacity-50"
            >
              {loading ? "..." : "Anmelden"}
            </button>
            <button
              type="button"
              onClick={() => setStep("phone")}
              className="w-full text-gray-500 text-sm hover:underline"
            >
              Zurück
            </button>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 mt-6">
          Noch kein Konto?{" "}
          <Link href="/register" className="text-purple-600 hover:underline font-medium">
            Registrieren
          </Link>
        </p>

        {devEnabled && (
          <div className="mt-6 border-t pt-4">
            <p className="text-xs text-center font-medium text-yellow-700 mb-3">
              🛠 Dev-Modus — schneller Login
            </p>
            {devUsers.length === 0 ? (
              <button
                onClick={seedDemo}
                disabled={devLoading}
                className="w-full bg-yellow-100 text-yellow-800 rounded-xl py-2 text-sm font-semibold hover:bg-yellow-200 disabled:opacity-50"
              >
                {devLoading ? "..." : "Demo-Daten anlegen"}
              </button>
            ) : (
              <div className="space-y-2">
                {devUsers.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => devLogin(u)}
                    disabled={devLoading}
                    className="w-full flex items-center gap-2 bg-yellow-50 border border-yellow-200 hover:bg-yellow-100 rounded-xl px-3 py-2 text-left disabled:opacity-50"
                  >
                    <span className="w-7 h-7 rounded-full bg-yellow-200 flex items-center justify-center text-xs font-bold text-yellow-800">
                      {u.name[0]}
                    </span>
                    <span className="flex-1">
                      <span className="text-sm font-medium text-gray-800">{u.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({u.role === "ADMIN" ? "Admin" : "Kid"})
                      </span>
                    </span>
                    <span className="text-xs text-yellow-700">→</span>
                  </button>
                ))}
                <button
                  onClick={seedDemo}
                  disabled={devLoading}
                  className="w-full text-xs text-gray-500 hover:text-gray-700 py-1"
                >
                  Demo-Daten nachladen
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <LegalFooter />
    </div>
  );
}
